import { useState } from "react";
import { RefreshCw, Link2, SlidersHorizontal, ChevronDown, ChevronUp } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { fetchInstallUrl } from "@/lib/api";
import { toast } from "sonner";
import PropertyConfigPanel from "@/components/PropertyConfigPanel";

const statusTone = {
  idle: "bg-slate-100 text-slate-700 border-slate-200",
  running: "bg-blue-100 text-blue-700 border-blue-200",
  success: "bg-emerald-100 text-emerald-700 border-emerald-200",
  error: "bg-rose-100 text-rose-700 border-rose-200",
};

export default function ConnectionPanel({ installInfo, connections, activePortalId, onSelectPortal, onSync, userRole = "admin" }) {
  const activeConnection = connections.find((connection) => connection.portal_id === activePortalId) || connections[0];
  const isAdmin = userRole !== "client";
  const [propConfigOpen, setPropConfigOpen] = useState(false);

  // Fetch a state-linked OAuth URL, then redirect — this links the portal to the current user
  const handleConnect = async () => {
    try {
      const payload = await fetchInstallUrl();
      window.location.href = payload.install_url;
    } catch {
      toast.error("Could not initiate HubSpot connection. Please try again.");
    }
  };

  return (
    <Card className="border-[#dddddd] shadow-sm" data-testid="hubspot-connection-panel">
      <CardHeader className="flex flex-col gap-4 border-b border-[#dddddd] bg-white lg:flex-row lg:items-center lg:justify-between">
        <CardTitle className="font-sans text-2xl text-[#323c44]" data-testid="hubspot-panel-title">
          HubSpot connection
        </CardTitle>

        <div className="flex flex-wrap gap-3">
          {isAdmin && (
            <Button
              onClick={handleConnect}
              className="h-10 rounded-full bg-[#323c44] px-4 text-white hover:bg-[#323c44]"
              data-testid="hubspot-connect-button"
            >
              <Link2 className="h-4 w-4" />
              Connect HubSpot
            </Button>
          )}
          {activeConnection ? (
            <Button
              variant="outline"
              className="h-10 rounded-full border-[#dddddd] px-4 text-[#323c44] hover:bg-[#efefef]"
              onClick={() => onSync(activeConnection.portal_id)}
              data-testid="hubspot-sync-button"
            >
              <RefreshCw className={cn("h-4 w-4", activeConnection.sync_status === "running" && "animate-spin")} />
              Refresh data
            </Button>
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="p-6">
        <div className="rounded-md border border-[#dddddd] bg-white p-4" data-testid="hubspot-connections-card">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">Connected portals</p>
              <p className="mt-1 text-sm text-[#4a5b68]" data-testid="hubspot-connections-count">
                {connections.length ? `${connections.length} portal${connections.length > 1 ? "s" : ""} available` : "No portal connected yet"}
              </p>
            </div>
            {activeConnection ? (
              <span className={cn("inline-flex items-center rounded-md border px-3 py-1 text-xs font-semibold capitalize", statusTone[activeConnection.sync_status] || statusTone.idle)} data-testid="hubspot-sync-status-badge">
                {activeConnection.sync_status}
              </span>
            ) : null}
          </div>

          <div className="grid gap-2">
            {connections.map((connection) => {
              const isActive = connection.portal_id === activePortalId;
              return (
                <button
                  key={connection.portal_id}
                  type="button"
                  className={cn(
                    "rounded-md border px-4 py-3 text-left transition-transform duration-200 hover:-translate-y-0.5",
                    isActive ? "border-[#57c1a9] bg-[#e8faf5] text-[#323c44]" : "border-[#dddddd] bg-white text-[#323c44] hover:bg-[#efefef]"
                  )}
                  onClick={() => onSelectPortal(connection.portal_id)}
                  data-testid={`portal-chip-${connection.portal_id}`}
                >
                  <span className="block text-sm font-semibold">{connection.portal_name || `Portal ${connection.portal_id}`}</span>
                  <span className="mt-1 block text-xs text-[#4a5b68]" data-testid={`portal-chip-status-${connection.portal_id}`}>
                    {connection.last_sync_at ? `Last sync ${new Date(connection.last_sync_at).toLocaleString()}` : "Not synced yet"}
                  </span>
                </button>
              );
            })}
          </div>

          {activeConnection?.last_error ? (
            <p className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="hubspot-last-error">
              {activeConnection.last_error}
            </p>
          ) : null}
        </div>
      </CardContent>

      {/* ── Property configuration ── */}
      {isAdmin && activeConnection && (
        <div className="border-t border-[#dddddd]">
          <button
            type="button"
            onClick={() => setPropConfigOpen((v) => !v)}
            className="flex w-full items-center justify-between px-6 py-4 text-left hover:bg-[#f5f5f5] transition"
          >
            <div className="flex items-center gap-3">
              <SlidersHorizontal className="h-4 w-4 text-[#119996]" />
              <div>
                <p className="text-sm font-semibold text-[#323c44]">Configure properties</p>
                <p className="text-xs text-[#4a5b68]">Choose which deal and line item fields are synced and available as filters</p>
              </div>
            </div>
            {propConfigOpen ? <ChevronUp className="h-4 w-4 text-[#7d8b96]" /> : <ChevronDown className="h-4 w-4 text-[#7d8b96]" />}
          </button>
          {propConfigOpen && (
            <div className="border-t border-[#dddddd] px-6 py-5">
              <PropertyConfigPanel
                portalId={activeConnection.portal_id}
                onSaved={() => onSync(activeConnection.portal_id)}
              />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
